import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/services/auth'
import { useViewMode } from '@/contexts/ViewModeContext'
import { FooterProvider } from '@/contexts/FooterContext'
import { VerificationBanner } from './VerificationBanner'
import { NavBar, type NavLink } from '@/components/layout/NavBar'
import { ThreePane } from '@/components/layout/ThreePane'
import { Footer } from '@/components/layout/Footer/Footer'
import { DimmingOverlay } from '@/components/DimmingOverlay/DimmingOverlay'
import { UserControls } from '@/components/composed/UserControls'
import { NotificationBell } from '@/components/NotificationBell'
import { userCollectionsUrl, tasksUrl } from '@/utils/urls'

export function Layout() {
  const { user, logout } = useAuth()
  const { viewMode } = useViewMode()
  const location = useLocation()

  // Determine active tab based on current location
  const getActiveTab = () => {
    if (location.pathname === '/') return 'home'
    // Support both /recent-discussions (new) and /recent-critiques (legacy)
    if (location.pathname === '/recent-discussions' || location.pathname === '/recent-critiques') return 'recent'
    // Support both /disc (new) and /crit (legacy) paths
    if (location.pathname === '/disc' || location.pathname.startsWith('/disc/') ||
        location.pathname === '/crit' || location.pathname.startsWith('/critique/') || location.pathname.startsWith('/crit/')) return 'discussion'
    if (location.pathname === '/collections') return 'collections'
    // Check for user-scoped collection paths: /u/:username/collections or /c/:collectionId
    if (location.pathname.startsWith('/u/') && location.pathname.includes('/collections')) return 'collections'
    if (location.pathname.startsWith('/c/')) return 'collections'
    if (location.pathname === '/tasks' || location.pathname.startsWith('/tasks/')) return 'tasks'
    if (location.pathname.startsWith('/admin')) return 'admin'
    if (location.pathname.startsWith('/dev')) return 'dev'
    return ''
  }

  const activeTab = getActiveTab()

  // Build navigation links array
  const navLinks: NavLink[] = []

  // 1. Home tab
  navLinks.push({ to: '/', label: 'Home', active: activeTab === 'home' })

  // 2. Tasks tab (Feature 078: User Tasks)
  if (user) {
    navLinks.push({
      to: tasksUrl(),
      label: 'Tasks',
      active: activeTab === 'tasks'
    })
  }

  // 3. Recent Discussions tab (Feature 067, renamed in Feature 890)
  navLinks.push({
    to: '/recent-discussions',
    label: 'Recent',
    active: activeTab === 'recent'
  })

  // 4. My Discussions tab (Feature 055, renamed in Feature 890)
  navLinks.push({
    to: '/disc',
    label: 'Discussions',
    active: activeTab === 'discussion'
  })

  // 4. Collections tab
  navLinks.push({
    to: user?.slug ? userCollectionsUrl(user.slug) : '/login',
    label: 'Collections',
    active: activeTab === 'collections'
  })


  // Logo component
  const logo = (
    <Link to="/" className="text-2xl font-bold text-foreground hover:text-primary transition-colors" style={{ textDecoration: 'none' }}>
      206mp
    </Link>
  )

  // User controls component
  const userControlsComponent = (
    <div className="flex gap-4 items-center">
      {user && <NotificationBell />}
      <UserControls user={user} onLogout={logout} />
    </div>
  )

  const headerContent = (
    <>
      <NavBar
        logo={logo}
        links={navLinks}
        userControls={userControlsComponent}
        className="px-8 py-3 "
      />
      {user && <VerificationBanner emailVerified={user.emailVerified || false} />}
    </>
  )

  const footerContent = (
    <Footer className="bg-surface-1 text-sm text-muted" />
  )

  return (
    <FooterProvider>
      <div className="relative h-screen">
        {/* Dimming overlay - covers entire layout including NavBar */}
        {/* Feature 057-lightbox-fullscreen-oneup */}
        <DimmingOverlay isActive={viewMode === 'dimmedLights'} />

        <ThreePane
          header={headerContent}
          footer={footerContent}
          className="relative h-screen bg-background text-foreground overflow-hidden"
        >
          <div className={"flex-1 flex flex-col overflow-hidden"}>
            <Outlet />
          </div>
        </ThreePane>
      </div>
    </FooterProvider>
  )
}
